import useSWR from 'swr';
import {
	getRecommendation,
	resolveRecomIdentity,
	toDpIARecommendationResponse,
} from '@/data/recom';
import type { RecomIdentity, RecommendationType } from '@/data/recom';
import type { DpIARecommendationResponse } from 'integration/generated/transactions/data-contracts';
import type { DpIARecommendationOptions } from 'integration/hooks/useGetDpIARecommendation';
import { useRecomIdentity } from 'integration/hooks/useRecomIdentity';

const DEFAULT_RECOMMENDATION_TYPES: RecommendationType[] = ['outfit', 'recommendations'];

const OPTIONAL_RECOMMENDATION_TYPES: RecommendationType[] = ['sameBrand', 'family'];

const RECOMMENDATION_LIMIT = 20;

const DATA_KEY_RECOM_IA_RECOMMENDATION = 'recomIaRecommendation';

export type RecomIARecommendationOptions = DpIARecommendationOptions & {
	partNumbers?: string[];
	withoutIdentity?: boolean;
};

export const recomIARecommendation = async (
	partNumber: string,
	filters: Array<any> = [],
	{
		types = DEFAULT_RECOMMENDATION_TYPES,
		sizes,
		sizeMode,
		partNumbers,
	}: RecomIARecommendationOptions = {},
	{ userId, sessionId }: RecomIdentity = {}
): Promise<DpIARecommendationResponse | undefined> => {
	const request = (type: RecommendationType[], withSizes: boolean) =>
		getRecommendation({
			type,
			params: {
				partnumber: partNumber,
				limit: RECOMMENDATION_LIMIT,
				...(userId ? { userId } : {}),
				...(sessionId ? { sessionId } : {}),
				...(withSizes && sizes ? { sizes } : {}),
				...(withSizes && sizeMode ? { sizeMode } : {}),
				...(partNumbers?.length ? { partnumbers: partNumbers } : {}),
			},
			filters: { values: filters },
		}).then(toDpIARecommendationResponse);
	const fallbackTypes = types.filter((type) => !OPTIONAL_RECOMMENDATION_TYPES.includes(type));
	const hasOptionalParams = fallbackTypes.length < types.length || !!sizes || !!sizeMode;

	try {
		return await request(types, true);
	} catch {
		return hasOptionalParams ? request(fallbackTypes, false).catch(() => undefined) : undefined;
	}
};

const recomIARecommendationKey = (
	partNumber: string,
	filters: Array<any>,
	userId: string | null | undefined,
	{ types, sizes, sizeMode, partNumbers, withoutIdentity }: RecomIARecommendationOptions
) =>
	[
		{
			partNumber,
			filters,
			userId,
			...(types ? { types } : {}),
			...(sizes ? { sizes } : {}),
			...(sizeMode ? { sizeMode } : {}),
			...(partNumbers?.length ? { partNumbers } : {}),
			...(withoutIdentity ? { withoutIdentity } : {}),
		},
		DATA_KEY_RECOM_IA_RECOMMENDATION,
	] as const;

export const useGetRecomIARecommendation = (
	partNumber?: string,
	filters: Array<any> = [],
	options: RecomIARecommendationOptions = {}
) => {
	const { identity, isLoadingIdentity } = useRecomIdentity();
	const { userId, sessionId } = resolveRecomIdentity(options.withoutIdentity ? {} : identity);
	const waitingIdentity = !options.withoutIdentity && isLoadingIdentity;

	const query = useSWR(
		partNumber && !waitingIdentity
			? recomIARecommendationKey(partNumber, filters, userId, options)
			: null,
		([props]) =>
			recomIARecommendation(
				props.partNumber,
				props.filters,
				{ types: props.types, sizes: props.sizes, sizeMode: props.sizeMode, partNumbers: props.partNumbers },
				{ userId: props.userId, sessionId }
			)
	);

	return { ...query, isLoading: query.isLoading || (!!partNumber && waitingIdentity) };
};
